import axios, { AxiosInstance } from 'axios';
import { createContext, ReactNode, useContext, useMemo } from 'react';

import ENVIRONMENT from '@/environment';
import { monolithApi } from '@/utils/api';
import { insightsTokenPath } from '@/utils/monolithApiRouteService';
import { isJwtExpired } from '@/utils/tokens';

import { usePageProps } from './PagePropsContext';

const ApiContext = createContext<AxiosInstance>(axios.create());

export const createApiInstance = (token: string) => {
  let bearerToken = token;
  let ongoingTokenRequest: Promise<void> | null = null;

  const axiosApiInstance = axios.create({
    baseURL: ENVIRONMENT.BITRISE_INSIGHTS_URL,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });

  axiosApiInstance.interceptors.request.use(
    async (config) => {
      if (isJwtExpired(bearerToken)) {
        if (ongoingTokenRequest === null) {
          ongoingTokenRequest = monolithApi.get(insightsTokenPath()).then(({ data: { token: newToken } }) => {
            bearerToken = newToken;
          });
        }
        await ongoingTokenRequest;
        ongoingTokenRequest = null;
      }

      config.headers.Authorization = `Bearer ${bearerToken}`;
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  return axiosApiInstance;
};

export const ApiProvider = ({ children }: { children: ReactNode }) => {
  const { traceToken } = usePageProps();
  const api = useMemo(() => createApiInstance(traceToken), [traceToken]);

  return <ApiContext.Provider value={api}>{children}</ApiContext.Provider>;
};

export const useApi = () => useContext(ApiContext);
